import { useState, useRef, useEffect, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Button, Space, InputNumber, App as AntdApp, Tooltip } from 'antd';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Plus, Trash2 } from 'lucide-react';
import type { SignatureRect } from '@/services/signingService';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// V1.0.63i: Mỗi item = 1 vị trí ký. 1 signer có thể có NHIỀU item.
export interface SignerPositionItem {
  id: string;          // ID duy nhất (UUID hoặc timestamp) để xóa từng vị trí
  signerId: string;
  rect: SignatureRect;
}

interface Props {
  file: File | Blob | string;
  signers: Array<{ id: string; fullName: string }>;
  /** V1.0.63i: List vị trí ký - cho phép 1 user nhiều vị trí trên cùng file */
  positionList: SignerPositionItem[];
  onAddPosition: (signerId: string, rect: SignatureRect) => void;
  onRemovePosition: (positionId: string) => void;
  selectedSignerId?: string | null;
  onSelectSigner?: (signerId: string) => void;
  readOnly?: boolean;
}

const SIGNER_COLORS = [
  '#f5222d', '#fa8c16', '#52c41a', '#6699CC', '#722ed1', '#eb2f96',
  '#13c2c2', '#a0d911', '#2f54eb', '#fa541c',
];

/**
 * V1.0.63i: PdfSignaturePlacer - multi-position version
 * Flow:
 * 1. Chọn signer (click avatar bên trái)
 * 2. Click vào bất kỳ vị trí nào trên PDF → thêm rect MỚI cho signer đó
 *    (không ghi đè rect cũ - cho phép cùng 1 signer có nhiều vị trí)
 * 3. Trên panel signer: hiện số vị trí + danh sách rect đã đặt + nút xóa từng cái
 */
export function PdfSignaturePlacer({
  file, signers, positionList, onAddPosition, onRemovePosition,
  selectedSignerId, onSelectSigner, readOnly,
}: Props) {
  const { message } = AntdApp.useApp();
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.2);
  const [pageSize, setPageSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const pageContainerRef = useRef<HTMLDivElement>(null);

  // Map signerId -> color
  const signerColors: Record<string, string> = {};
  signers.forEach((s, i) => { signerColors[s.id] = SIGNER_COLORS[i % SIGNER_COLORS.length]; });

  // Đếm số vị trí của mỗi signer
  const positionsPerSigner = useMemo(() => {
    const map = new Map<string, SignerPositionItem[]>();
    for (const item of positionList) {
      if (!map.has(item.signerId)) map.set(item.signerId, []);
      map.get(item.signerId)!.push(item);
    }
    return map;
  }, [positionList]);

  // V1.0.64: Tự auto-focus signer ĐẦU TIÊN (= người ký đầu trong thứ tự ký).
  // Trước đây chỉ auto-select khi selectedSignerId rỗng, nhưng nếu parent set sai signer
  // (vd: AntD Select trả không đúng thứ tự ký) thì cần ghi đè khi component mount/signers đổi.
  // Reset về signers[0] khi:
  //   - chưa có selectedSignerId, hoặc
  //   - selectedSignerId không nằm trong list signers hiện tại
  useEffect(() => {
    if (signers.length === 0) return;
    const inList = selectedSignerId && signers.some(s => s.id === selectedSignerId);
    if (!inList) {
      onSelectSigner?.(signers[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signers]);

  const handlePageClick = (e: React.MouseEvent) => {
    if (readOnly) return;
    // V1.0.64-fix: Defense-in-depth — bỏ qua nếu click bắt nguồn từ <button>
    // (nút X xóa position). Đảm bảo không tạo rect mới khi user click X.
    const targetEl = e.target as HTMLElement;
    if (targetEl.closest('button')) return;
    if (!selectedSignerId) {
      message.warning('Vui lòng chọn người ký trước khi đặt vị trí');
      return;
    }
    const target = e.currentTarget as HTMLDivElement;
    const rect = target.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    const xRatio = clickX / rect.width;
    const yRatio = clickY / rect.height;
    const wRatio = 0.16;
    const hRatio = 0.056;

    onAddPosition(selectedSignerId, {
      page: currentPage,
      xRatio: Math.max(0, Math.min(1 - wRatio, xRatio - wRatio / 2)),
      yRatio: Math.max(0, Math.min(1 - hRatio, yRatio - hRatio / 2)),
      wRatio, hRatio,
    });
  };

  const onDocumentLoad = ({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
    setCurrentPage(1);
  };

  const onPageLoad = (page: any) => {
    setPageSize({ width: page.width, height: page.height });
  };

  // Chỉ render rects thuộc trang hiện tại
  const rectsOnCurrentPage = positionList.filter(item =>
    (item.rect.page === currentPage) || (item.rect.page === 0 && currentPage === numPages)
  );

  return (
    <div style={{ display: 'flex', gap: 12, height: '100%', minHeight: 500 }}>
      {/* Signer picker bên trái */}
      {!readOnly && signers.length > 0 && (
        <div style={{ width: 240, flexShrink: 0, overflowY: 'auto' }}>
          <div style={{ fontSize: 12, color: '#7d8a98', marginBottom: 8, fontWeight: 600 }}>
            Người ký ({positionList.length} vị trí)
          </div>
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            {signers.map((s) => {
              const myPositions = positionsPerSigner.get(s.id) ?? [];
              const isSelected = selectedSignerId === s.id;
              const color = signerColors[s.id];
              return (
                <div
                  key={s.id}
                  style={{
                    border: `2px solid ${isSelected ? color : '#e1e5eb'}`,
                    background: isSelected ? `${color}10` : '#fff',
                    borderRadius: 6,
                    fontSize: 12,
                    overflow: 'hidden',
                  }}
                >
                  {/* Header signer - click để chọn */}
                  <div
                    onClick={() => onSelectSigner?.(s.id)}
                    style={{
                      padding: '8px 10px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <div style={{
                      width: 10, height: 10, borderRadius: '50%',
                      background: color, flexShrink: 0,
                    }} />
                    <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.fullName}
                    </div>
                    <span style={{
                      fontSize: 10,
                      color: myPositions.length > 0 ? '#52c41a' : '#8a95a5',
                      fontWeight: 600,
                      background: myPositions.length > 0 ? '#f6ffed' : '#f5f5f5',
                      padding: '1px 6px',
                      borderRadius: 8,
                    }}>
                      {myPositions.length} vị trí
                    </span>
                  </div>

                  {/* Danh sách rect đã đặt + nút xóa từng cái */}
                  {myPositions.length > 0 && (
                    <div style={{
                      borderTop: `1px dashed ${color}40`,
                      padding: '6px 10px',
                      background: '#fff',
                    }}>
                      {myPositions.map((p, idx) => (
                        <div key={p.id} style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          padding: '3px 0', fontSize: 11, color: '#5b6875',
                        }}>
                          <span style={{ color, fontWeight: 600 }}>#{idx + 1}</span>
                          <span style={{ flex: 1 }}>Trang {p.rect.page}</span>
                          <Tooltip title="Xóa vị trí này">
                            <Button
                              type="text" size="small" danger
                              icon={<Trash2 size={11} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                onRemovePosition(p.id);
                              }}
                              style={{ height: 18, padding: '0 4px' }}
                            />
                          </Tooltip>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </Space>

          <div style={{ fontSize: 11, color: '#8a95a5', marginTop: 12, lineHeight: 1.6 }}>
            💡 <b>Hướng dẫn:</b><br />
            1. Chọn người ký ở danh sách bên trên<br />
            2. Click vào PDF để đặt vị trí ký<br />
            3. <b>Click nhiều lần</b> → đặt nhiều vị trí cho cùng 1 người<br />
            4. Click 🗑 để xóa từng vị trí
          </div>
        </div>
      )}

      {/* PDF viewer */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #e1e5eb',
        borderRadius: 6,
        background: '#f8f9fa',
        overflow: 'hidden',
      }}>
        {/* Toolbar */}
        <div style={{
          padding: '8px 12px',
          borderBottom: '1px solid #e1e5eb',
          background: '#fff',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <Space size={4}>
            <Button size="small" icon={<ChevronLeft size={14} />}
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(p => p - 1)} />
            <InputNumber size="small" value={currentPage} min={1} max={numPages || 1}
              onChange={(v) => v && setCurrentPage(v)} style={{ width: 60 }} />
            <span style={{ fontSize: 12, color: '#5b6875' }}>/ {numPages}</span>
            <Button size="small" icon={<ChevronRight size={14} />}
              disabled={currentPage >= numPages}
              onClick={() => setCurrentPage(p => p + 1)} />
          </Space>
          <div style={{ flex: 1 }} />
          <Space size={4}>
            <Button size="small" icon={<ZoomOut size={14} />}
              onClick={() => setScale(s => Math.max(0.5, s - 0.2))} />
            <span style={{ fontSize: 12, color: '#5b6875', minWidth: 40, textAlign: 'center' }}>
              {Math.round(scale * 100)}%
            </span>
            <Button size="small" icon={<ZoomIn size={14} />}
              onClick={() => setScale(s => Math.min(2.5, s + 0.2))} />
          </Space>
        </div>

        {/* PDF page */}
        <div ref={pageContainerRef} style={{
          flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center',
          padding: 12,
        }}>
          <Document file={file} onLoadSuccess={onDocumentLoad}>
            <div
              style={{
                position: 'relative',
                display: 'inline-block',
                cursor: readOnly ? 'default' : (selectedSignerId ? 'crosshair' : 'not-allowed'),
                userSelect: 'none',
              }}
              onMouseDown={handlePageClick}
            >
              <div style={{ pointerEvents: 'none' }}>
                <Page
                  pageNumber={currentPage}
                  scale={scale}
                  onLoadSuccess={onPageLoad}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </div>

              {/* Overlay rects - render từng item riêng */}
              {rectsOnCurrentPage.map((item) => {
                const signer = signers.find(s => s.id === item.signerId);
                if (!signer) return null;
                const color = signerColors[item.signerId] ?? '#6699CC';
                // Index trong danh sách của signer này (để hiện #1, #2...)
                const myList = positionsPerSigner.get(item.signerId) ?? [];
                const orderIdx = myList.findIndex(p => p.id === item.id);
                return (
                  <div
                    key={item.id}
                    // V1.0.64-fix: Chặn event bubble lên parent để click vào trong rect
                    //   KHÔNG tạo position mới đè lên. User chỉ xóa qua nút X.
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute',
                      left: `${item.rect.xRatio * 100}%`,
                      top: `${item.rect.yRatio * 100}%`,
                      width: `${item.rect.wRatio * 100}%`,
                      height: `${item.rect.hRatio * 100}%`,
                      border: `2px solid ${color}`,
                      background: `${color}20`,
                      borderRadius: 3,
                      pointerEvents: 'auto',
                      boxSizing: 'border-box',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 10,
                      color: color,
                      fontWeight: 600,
                      textShadow: '0 0 4px white',
                    }}
                  >
                    {/* Badge số thứ tự góc trái trên */}
                    <div style={{
                      position: 'absolute',
                      top: -10, left: -2,
                      background: color, color: '#fff',
                      borderRadius: 8, fontSize: 9,
                      padding: '1px 6px', lineHeight: '12px',
                      fontWeight: 700,
                    }}>
                      #{orderIdx + 1}
                    </div>
                    <div>✍ {signer.fullName}</div>
                    <div style={{ fontSize: 8, opacity: 0.7 }}>(vị trí chữ ký)</div>
                    {/* Nút xóa nhỏ ở góc phải trên */}
                    {!readOnly && (
                      <button
                        // V1.0.64-fix: Parent dùng onMouseDown (trigger TRƯỚC onClick) để bắt click PDF.
                        // → e.stopPropagation() trong onClick KHÔNG kịp ngăn parent.
                        // Phải stopPropagation ở onMouseDown + preventDefault để hủy chain.
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemovePosition(item.id);
                        }}
                        style={{
                          position: 'absolute',
                          top: -10, right: -2,
                          width: 18, height: 18,
                          borderRadius: '50%', border: 'none',
                          background: '#fff', color: '#cf1322',
                          cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        }}
                        title="Xóa vị trí này"
                      >×</button>
                    )}
                  </div>
                );
              })}
            </div>
          </Document>
        </div>
      </div>
    </div>
  );
}
